import type { Slot, SlotComponent } from "@puckeditor/core";
import type { BaseComponentProps } from '../../types/puck';
import type { ColorRole } from '../../lib/color-role';
import { EmptyBlock } from "../empty-block";
import { LUCIDE_ICONS } from "../../lib/lucide-icons";
import React from "react";
import { SmartLink } from "../smart-link";
import { Button as UiButton } from "../ui/button";
import { cn } from "../../lib/utils";
import { colorRoleClass } from '../../lib/color-role';

/**
 * Atomic building blocks.
 *
 * The section components each render a whole opinionated band — hero, gallery, pricing. These
 * are the pieces underneath that, for laying out something the sections don't cover: one
 * heading, one button, one image. They deliberately carry no section padding of their own, so
 * they stack inside a page without fighting the spacing rhythm the sections establish.
 */

type Align = "left" | "center" | "right";
type Width = "narrow" | "normal" | "wide" | "full";

const ALIGN_SELF: Record<Align, string> = {
    center: "jobsite:mx-auto",
    left: "jobsite:mr-auto",
    right: "jobsite:ml-auto",
  },
  ALIGN_TEXT: Record<Align, string> = {
    center: "jobsite:text-center",
    left: "jobsite:text-left",
    right: "jobsite:text-right",
  },
  WIDTH: Record<Width, string> = {
    full: "jobsite:max-w-none",
    narrow: "jobsite:max-w-2xl",
    normal: "jobsite:max-w-4xl",
    wide: "jobsite:max-w-6xl",
  },

  /** Shared outer wrapper: container, horizontal rhythm, and vertical spacing. */
  Block = ({
    id,
    className,
    align = "left",
    width = "normal",
    spacing = "normal",
    children,
  }: {
    id?: string;
    className?: string;
    align?: Align;
    width?: Width;
    spacing?: "none" | "tight" | "normal" | "loose";
    children: React.ReactNode;
  }) => (
    <div
      id={id}
      className={cn(
        "jobsite:container jobsite:mx-auto jobsite:px-4",
        spacing === "none" && "jobsite:py-0",
        spacing === "tight" && "jobsite:py-3",
        spacing === "normal" && "jobsite:py-6",
        spacing === "loose" && "jobsite:py-12",
        className,
      )}
    >
      <div className={cn(WIDTH[width], ALIGN_SELF[align], ALIGN_TEXT[align])}>{children}</div>
    </div>
  ),
  HeadingBlock: React.FC<HeadingBlockProps> = ({
    text,
    level = "h2",
    size = "medium",
    color = "auto",
    align = "left",
    width = "normal",
    spacing = "normal",
    id,
    className,
  }) => {
    // The tag is a separate choice from the size: document outline is an SEO concern, and
    // Visual weight is a design one. Tying them together forces one to be wrong.
    const Tag = level;

    return (
      <Block id={id} className={className} align={align} width={width} spacing={spacing}>
        <Tag
          className={cn(
            "jobsite:font-heading jobsite:tracking-tight jobsite:text-balance",
            colorRoleClass(color, "strong"),
            size === "small" && "jobsite:text-xl jobsite:sm:text-2xl",
            size === "medium" && "jobsite:text-2xl jobsite:sm:text-3xl jobsite:md:text-4xl",
            size === "large" && "jobsite:text-3xl jobsite:sm:text-4xl jobsite:md:text-5xl",
          )}
        >
          {text}
        </Tag>
      </Block>
    );
  },
  TextBlock: React.FC<TextBlockProps> = ({
    content,
    size = "medium",
    color = "auto",
    align = "left",
    width = "narrow",
    spacing = "normal",
    id,
    className,
  }) => {
    // Blank line between paragraphs, same convention as the content block.
    // Tolerate a ReactNode in `content`: the editors swap the string for an
    // Inline-editable element. Strings keep the exact old semantics; an
    // Element renders as one block.
    const paragraphs =
        typeof content === "string" || !content
          ? (content || "").split("\n\n").filter((entry) => entry.trim())
          : null,
      paragraphClass = cn(
        "jobsite:text-pretty jobsite:leading-relaxed",
        colorRoleClass(color, "quiet"),
        size === "small" && "jobsite:text-sm",
        size === "medium" && "jobsite:text-base jobsite:sm:text-lg",
        size === "large" && "jobsite:text-lg jobsite:sm:text-xl",
      );

    return (
      <Block id={id} className={className} align={align} width={width} spacing={spacing}>
        <div className="jobsite:space-y-4">
          {paragraphs ? (
            paragraphs.map((paragraph, index) => (
              <p key={index} className={paragraphClass}>
                {paragraph}
              </p>
            ))
          ) : (
            <p className={paragraphClass}>{content}</p>
          )}
        </div>
      </Block>
    );
  },
  ButtonBlock: React.FC<ButtonBlockProps> = ({
    label,
    url,
    variant = "primary",
    size = "medium",
    align = "left",
    spacing = "normal",
    id,
    className,
  }) => (
    <Block id={id} className={className} align={align} width="full" spacing={spacing}>
      <UiButton
        asChild
        variant={variant === "primary" ? "default" : (variant === "outline" ? "outline" : "ghost")}
        className={cn(
          "jobsite:font-semibold",
          size === "small" && "jobsite:px-5 jobsite:py-4 jobsite:text-sm",
          size === "medium" && "jobsite:px-7 jobsite:py-5 jobsite:text-base",
          size === "large" && "jobsite:px-9 jobsite:py-6 jobsite:text-lg",
        )}
      >
        <SmartLink to={url || "#contact"}>{label}</SmartLink>
      </UiButton>
    </Block>
  ),
  ImageBlock: React.FC<ImageBlockProps> = ({
    imageUrl,
    alt,
    caption,
    width = "normal",
    align = "center",
    frame = "none",
    spacing = "normal",
    id,
    className,
  }) => {
    if (!imageUrl) {
      return <EmptyBlock label="No image yet — choose one in the panel" />;
    }

    return (
      <Block id={id} className={className} align={align} width={width} spacing={spacing}>
        <figure className="jobsite:m-0">
          <img
            src={imageUrl}
            alt={alt || ""}
            crossOrigin="anonymous"
            loading="lazy"
            className={cn(
              "jobsite:h-auto jobsite:w-full",
              frame === "shadow" && "jobsite:shadow-lg",
              frame === "border" && "jobsite:border jobsite:border-border",
            )}
            style={{ borderRadius: "var(--jobsite-radius)" }}
          />
          {caption ? (
            <figcaption className="jobsite:mt-3 jobsite:text-sm jobsite:text-muted-foreground">
              {caption}
            </figcaption>
          ) : null}
        </figure>
      </Block>
    );
  },
  SpacerBlock: React.FC<SpacerBlockProps> = ({ height = "medium", id, className }) => (
    <div
      id={id}
      aria-hidden="true"
      className={cn(
        height === "small" && "jobsite:h-6",
        height === "medium" && "jobsite:h-12",
        height === "large" && "jobsite:h-20",
        height === "xlarge" && "jobsite:h-32",
        className,
      )}
    />
  ),
  DividerBlock: React.FC<DividerBlockProps> = ({
    width = "normal",
    spacing = "normal",
    id,
    className,
  }) => (
    <Block id={id} className={className} align="center" width={width} spacing={spacing}>
      <hr className="jobsite:border-0 jobsite:border-t jobsite:border-border" />
    </Block>
  ),
  /**
   * One small labelled chip. The same thing a hero eyebrow, a trust indicator and a CTA proof
   * chip have always been — named for its shape so it serves all three instead of three bespoke
   * arrays.
   */
  BadgeBlock: React.FC<BadgeBlockProps> = ({
    text,
    icon,
    tone = "accent",
    size = "medium",
    align = "left",
    spacing = "tight",
    id,
    className,
  }) => {
    const IconComponent = icon
      ? LUCIDE_ICONS[icon]
      : undefined;

    return (
      <Block id={id} align={align} spacing={spacing} className={className}>
        <span
          className={cn(
            "jobsite:inline-flex jobsite:items-center jobsite:gap-2 jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em]",
            size === "small" ? "jobsite:px-3 jobsite:py-1 jobsite:text-[11px]" : "jobsite:px-4 jobsite:py-1.5 jobsite:text-xs",
            tone === "accent" && "jobsite:border jobsite:border-accent/25 jobsite:bg-accent/15 jobsite:text-accent",
            tone === "neutral" && "jobsite:border jobsite:border-border jobsite:bg-muted jobsite:text-foreground",
            // Over photography the chip has to carry its own contrast.
            tone === "onMedia" &&
              "jobsite:border jobsite:border-white/28 jobsite:bg-black/15 jobsite:text-white jobsite:backdrop-blur-md",
            ALIGN_SELF[align],
          )}
          style={{ borderRadius: "var(--jobsite-radius)" }}
        >
          {IconComponent ? <IconComponent className="jobsite:h-3.5 jobsite:w-3.5" /> : null}
          {text}
        </span>
      </Block>
    );
  },
  /**
   * Arrangement only: no background, no padding of its own — that is SectionBlock's job. This
   * exists because a slot stacks its children, and a button pair or a row of badges has to sit
   * side by side.
   */
  GroupBlock: React.FC<GroupBlockRenderProps> = ({
    content: Content,
    direction = "row",
    gap = "normal",
    wrap = "yes",
    align = "left",
    spacing = "tight",
    id,
    className,
  }) => (
    <Block id={id} align={align} spacing={spacing} className={className}>
      {/* The editor finds a droppable slot by this attribute; published pages
          carry it harmlessly. */}
      <div data-sp-slot={`${id ?? ""}::content`}>
      <Content
        className={cn(
          // Stable hook so the editor can show an empty container; published
          // Pages style nothing off it.
          "jobsite-slot",
          "jobsite:flex",
          direction === "row" ? "jobsite:flex-row" : "jobsite:flex-col",
          direction === "row" && wrap === "yes" && "jobsite:flex-wrap",
          gap === "tight" && "jobsite:gap-2",
          gap === "normal" && "jobsite:gap-4",
          gap === "loose" && "jobsite:gap-8",
          direction === "row" && align === "left" && "jobsite:justify-start",
          direction === "row" && align === "center" && "jobsite:justify-center",
          direction === "row" && align === "right" && "jobsite:justify-end",
          direction === "row" && "jobsite:items-center",
          direction === "column" && align === "center" && "jobsite:items-center",
          direction === "column" && align === "right" && "jobsite:items-end",
        )}
      />
      </div>
    </Block>
  ),
  SectionBlock: React.FC<SectionBlockRenderProps> = ({
    content: Content,
    background = "default",
    padding = "normal",
    anchorId,
    id,
    className,
  }) => (
    <section
      id={anchorId?.trim() || id}
      className={cn(
        background === "default" && "jobsite:bg-background",
        background === "muted" && "jobsite:bg-muted/50",
        background === "primary" && "jobsite:bg-primary/5",
        padding === "tight" && "jobsite:py-8",
        padding === "normal" && "jobsite:py-16 jobsite:md:py-24",
        padding === "loose" && "jobsite:py-24 jobsite:md:py-32",
        className,
      )}
    >
      <div data-sp-slot={`${id ?? ""}::content`}>
        <Content className="jobsite-slot" />
      </div>
    </section>
  );

interface HeadingBlockProps extends BaseComponentProps {
  /** Colour as a role that resolves through the theme; `auto` follows the frame. */
  color?: ColorRole;
  text: string;
  level?: "h1" | "h2" | "h3" | "h4";
  size?: "small" | "medium" | "large";
  align?: Align;
  width?: Width;
  spacing?: "none" | "tight" | "normal" | "loose";
}

interface TextBlockProps extends BaseComponentProps {
  content: string;
  /** Colour as a role that resolves through the theme; `auto` follows the frame. */
  color?: ColorRole;
  size?: "small" | "medium" | "large";
  align?: Align;
  width?: Width;
  spacing?: "none" | "tight" | "normal" | "loose";
}

interface ButtonBlockProps extends BaseComponentProps {
  label: string;
  url: string;
  variant?: "primary" | "outline" | "ghost";
  size?: "small" | "medium" | "large";
  align?: Align;
  spacing?: "none" | "tight" | "normal" | "loose";
}

interface ImageBlockProps extends BaseComponentProps {
  imageUrl: string;
  alt: string;
  caption?: string;
  width?: Width;
  align?: Align;
  frame?: "none" | "shadow" | "border";
  spacing?: "none" | "tight" | "normal" | "loose";
}

interface SpacerBlockProps extends BaseComponentProps {
  height?: "small" | "medium" | "large" | "xlarge";
}

interface DividerBlockProps extends BaseComponentProps {
  width?: Width;
  spacing?: "none" | "tight" | "normal" | "loose";
}

interface BadgeBlockProps extends BaseComponentProps {
  text: string;
  /** Lucide-react icon name, e.g. "shield-check". */
  icon?: string;
  tone?: "accent" | "neutral" | "onMedia";
  size?: "small" | "medium";
  align?: Align;
  spacing?: "none" | "tight" | "normal" | "loose";
}

interface GroupBlockProps extends BaseComponentProps {
  /** Puck slot — whatever is being arranged. */
  content: Slot;
  direction?: "row" | "column";
  gap?: "tight" | "normal" | "loose";
  wrap?: "yes" | "no";
  align?: Align;
  spacing?: "none" | "tight" | "normal" | "loose";
}

type GroupBlockRenderProps = Omit<GroupBlockProps, "content"> & { content: SlotComponent };

interface SectionBlockProps extends BaseComponentProps {
  /** Puck slot — anything dropped inside this section. */
  content: Slot;
  background?: "default" | "muted" | "primary" | "none";
  padding?: "none" | "tight" | "normal" | "loose";
  /**
   * Anchor for links to target, e.g. "contact" so a CTA pointing at #contact lands here.
   * Without one, a section built from primitives can't be linked to.
   */
  anchorId?: string;
}

/**
 * `Slot` is the field's data shape; what Puck hands the renderer is a component. They are
 * different types, so the render signature swaps the one for the other.
 */
type SectionBlockRenderProps = Omit<SectionBlockProps, "content"> & { content: SlotComponent };

export type {
  HeadingBlockProps,
  TextBlockProps,
  ButtonBlockProps,
  ImageBlockProps,
  SpacerBlockProps,
  DividerBlockProps,
  BadgeBlockProps,
  GroupBlockProps,
  GroupBlockRenderProps,
  SectionBlockProps,
  SectionBlockRenderProps,
};
export {
  HeadingBlock,
  TextBlock,
  ButtonBlock,
  ImageBlock,
  SpacerBlock,
  DividerBlock,
  BadgeBlock,
  GroupBlock,
  SectionBlock,
};
